import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Edit2, Trash2, Folder, FolderTree, Layers, ChevronRight, ChevronDown,
  Search, RefreshCw, ArrowLeft, CheckCircle2, AlertCircle, X, Bot, Package,
} from 'lucide-react'
import { categoriesApi, Category, CreateCategoryDto, UpdateCategoryDto } from '@/services/categoriesApi'
import { toast } from 'react-hot-toast'
import CatalogNavTabs from '@/components/catalog/CatalogNavTabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function flattenForSelect(
  cats: Category[],
  excludeId: string | null = null,
  depth = 0
): { id: string; name: string; depth: number }[] {
  const result: { id: string; name: string; depth: number }[] = []
  for (const cat of cats) {
    if (excludeId && cat.id === excludeId) continue
    result.push({ id: cat.id, name: cat.name, depth })
    if (cat.children?.length) {
      result.push(...flattenForSelect(cat.children, excludeId, depth + 1))
    }
  }
  return result
}

export default function CategoriesSettings() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)

  // Form states
  const [formName, setFormName] = useState('')
  const [formDesc, setFormDesc] = useState('')
  const [formParentId, setFormParentId] = useState('')
  const [formSortOrder, setFormSortOrder] = useState(0)
  const [formIsActive, setFormIsActive] = useState(true)

  const { data: categories = [], isLoading, refetch } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryDto) => categoriesApi.create(data),
    onSuccess: (newCat) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setIsFormOpen(false)
      if (newCat.parentId) {
        setExpandedIds((prev) => new Set(prev).add(newCat.parentId!))
      }
      toast.success(`Categoría "${newCat.name}" creada`)
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Error al crear categoría'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryDto }) => categoriesApi.update(id, data),
    onSuccess: (updatedCat) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setIsFormOpen(false)
      toast.success(`Categoría "${updatedCat.name}" actualizada`)
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Error al actualizar categoría'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setCategoryToDelete(null)
      toast.success('Categoría eliminada')
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Error al eliminar categoría'),
  })

  const handleOpenCreate = (parentId: string = '') => {
    setEditingCategory(null)
    setFormName('')
    setFormDesc('')
    setFormParentId(parentId)
    setFormSortOrder(0)
    setFormIsActive(true)
    setIsFormOpen(true)
  }

  const handleOpenEdit = (category: Category) => {
    setEditingCategory(category)
    setFormName(category.name)
    setFormDesc(category.description || '')
    setFormParentId(category.parentId || '')
    setFormSortOrder(category.sortOrder ?? 0)
    setFormIsActive(category.isActive ?? true)
    setIsFormOpen(true)
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleExpandAll = () => {
    const allIds = new Set<string>()
    const collect = (cats: Category[]) => {
      cats.forEach((c) => {
        if (c.children?.length) {
          allIds.add(c.id)
          collect(c.children)
        }
      })
    }
    collect(categories)
    setExpandedIds(allIds)
  }

  const handleCollapseAll = () => {
    setExpandedIds(new Set())
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formName.trim()) {
      toast.error('El nombre de la categoría es obligatorio')
      return
    }

    if (editingCategory) {
      updateMutation.mutate({
        id: editingCategory.id,
        data: {
          name: formName.trim(),
          description: formDesc.trim() || undefined,
          parentId: formParentId ? formParentId : null,
          sortOrder: formSortOrder,
          isActive: formIsActive,
        },
      })
    } else {
      createMutation.mutate({
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        parentId: formParentId ? formParentId : undefined,
        sortOrder: formSortOrder,
        isActive: formIsActive,
      })
    }
  }

  // Hierarchical stats
  const { totalCount, rootCount, subCount, activeCount } = useMemo(() => {
    let total = 0
    let root = categories.length
    let sub = 0
    let active = 0

    const countCat = (cat: Category, isRoot: boolean) => {
      total += 1
      if (!isRoot) sub += 1
      if (cat.isActive) active += 1
      if (cat.children?.length) {
        cat.children.forEach((c) => countCat(c, false))
      }
    }

    categories.forEach((cat) => countCat(cat, true))

    return {
      totalCount: total,
      rootCount: root,
      subCount: sub,
      activeCount: active,
    }
  }, [categories])

  const categoryOptions = useMemo(
    () => flattenForSelect(categories, editingCategory?.id ?? null),
    [categories, editingCategory]
  )

  const matchesSearch = (cat: Category): boolean => {
    if (!search) return true
    const q = search.toLowerCase()
    if (cat.name.toLowerCase().includes(q)) return true
    if (cat.description?.toLowerCase().includes(q)) return true
    if (cat.children?.some(matchesSearch)) return true
    return false
  }

  const filterTree = (cats: Category[]): Category[] =>
    cats
      .filter(matchesSearch)
      .map((cat) => ({ ...cat, children: cat.children ? filterTree(cat.children) : [] }))

  const visibleCategories = useMemo(
    () => (search ? filterTree(categories) : categories),
    [categories, search]
  )

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <div className="space-y-6 pb-12">
      {/* Navigation Tabs */}
      <CatalogNavTabs />

      {/* Hero Section */}
      <section className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 shadow-sm">
        <div className="grid gap-6 px-5 py-6 lg:grid-cols-[1.4fr_0.8fr] lg:px-8 lg:py-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#075E54]">
              <FolderTree className="h-3.5 w-3.5" />
              Catálogo & Navegación
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900 lg:text-4xl">Categorías</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 lg:text-base">
                Organiza tu inventario en departamentos y subcategorías. El asistente de WhatsApp utilizará esta jerarquía para sugerir productos a tus clientes de manera intuitiva.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() => handleOpenCreate()}
                className="inline-flex items-center gap-2 rounded-full bg-[#075E54] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#064E46]"
              >
                <Plus className="h-4 w-4" />
                Nueva categoría
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-50"
              >
                <RefreshCw className="h-4 w-4" />
                Refrescar
              </Button>
              <Link
                to="/app/catalog"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver a vitrina
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <MetricCard label="Total" value={String(totalCount)} icon={FolderTree} />
              <MetricCard label="Principales" value={String(rootCount)} icon={Folder} />
              <MetricCard label="Subcategorías" value={String(subCount)} icon={Layers} />
              <MetricCard label="Activas" value={String(activeCount)} icon={CheckCircle2} />
            </div>
          </div>

          {/* Quick tips panel */}
          <div className="rounded-[1.75rem] border border-white/70 bg-white/80 p-4 shadow-lg backdrop-blur">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Estructura</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {rootCount} departamentos principales
                </p>
              </div>
              <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-[#075E54]">
                Multitenant
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-600">
                Las categorías se integran directamente con el catálogo público y los triggers de búsqueda del bot.
              </p>
              <div className="rounded-2xl bg-slate-950 p-4 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Buenas prácticas</p>
                <ul className="mt-3 space-y-2 text-sm text-white/85">
                  <li className="flex items-start gap-2">
                    <Bot className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    Agrega descripciones breves para enriquecer las respuestas de la IA.
                  </li>
                  <li className="flex items-start gap-2">
                    <Folder className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    Usa subcategorías para no saturar los menús iniciales de WhatsApp.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    Desactiva temporalmente categorías fuera de temporada sin borrarlas.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar / Search Filter */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o descripción..."
            className="pl-10 pr-9 rounded-xl border-slate-200 text-sm h-10"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full"
              title="Limpiar búsqueda"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExpandAll}
            className="text-xs rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Expandir todas
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCollapseAll}
            className="text-xs rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Colapsar todas
          </Button>
        </div>
      </section>

      {/* Categories Tree View */}
      {isLoading ? (
        <section className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-center gap-4">
              <div className="h-10 w-10 rounded-xl bg-slate-200 shrink-0" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-40 rounded bg-slate-200" />
                <div className="h-3 w-24 rounded bg-slate-100" />
              </div>
            </div>
          ))}
        </section>
      ) : visibleCategories.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-[#075E54] flex items-center justify-center mx-auto mb-4">
            <FolderTree className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">
            {search ? 'Sin resultados para la búsqueda' : 'No tienes categorías creadas'}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {search
              ? `No encontramos ninguna categoría o subcategoría que coincida con "${search}".`
              : 'Comienza creando la primera categoría para organizar tus productos y optimizar la experiencia de compra en WhatsApp.'}
          </p>
          <div className="mt-5 flex justify-center gap-3">
            {search ? (
              <Button type="button" variant="outline" onClick={() => setSearch('')} className="rounded-xl">
                Limpiar búsqueda
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => handleOpenCreate()}
                className="bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl shadow-sm px-5"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Crear primera categoría
              </Button>
            )}
          </div>
        </section>
      ) : (
        <section className="space-y-3">
          {visibleCategories.map((cat) => {
            const hasChildren = Boolean(cat.children?.length)
            const isExpanded = expandedIds.has(cat.id) || Boolean(search)

            return (
              <article
                key={cat.id}
                className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-all overflow-hidden"
              >
                {/* Root Category Row */}
                <div className={`p-4 sm:p-5 flex items-center justify-between gap-3 ${hasChildren && isExpanded ? 'border-b border-slate-100' : ''}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      type="button"
                      onClick={() => hasChildren && toggleExpanded(cat.id)}
                      disabled={!hasChildren}
                      className={`p-1 text-slate-400 hover:text-slate-700 transition-colors rounded-lg ${
                        !hasChildren ? 'opacity-20 cursor-default' : 'hover:bg-slate-100 cursor-pointer'
                      }`}
                      aria-label={isExpanded ? 'Colapsar subcategorías' : 'Expandir subcategorías'}
                    >
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-[#075E54]" /> : <ChevronRight className="w-5 h-5" />}
                    </button>

                    <div className="w-11 h-11 rounded-xl bg-emerald-50 text-[#075E54] flex items-center justify-center shrink-0">
                      <Folder className="w-5 h-5" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-slate-900 text-base truncate">{cat.name}</h3>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full shrink-0 ${
                            cat.isActive
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}
                        >
                          {cat.isActive ? 'Activa' : 'Inactiva'}
                        </span>
                        <code className="text-[11px] font-mono text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                          /{cat.slug || generateSlug(cat.name)}
                        </code>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                        <span>Orden: <strong>{cat.sortOrder ?? 0}</strong></span>
                        <span>•</span>
                        <span className="flex items-center gap-1 text-slate-600">
                          <Package className="w-3.5 h-3.5 text-slate-400" />
                          {cat.productCount ?? 0} {cat.productCount === 1 ? 'producto' : 'productos'}
                        </span>
                        {hasChildren && (
                          <>
                            <span>•</span>
                            <span className="font-medium text-[#075E54]">
                              {cat.children!.length} {cat.children!.length === 1 ? 'subcategoría' : 'subcategorías'}
                            </span>
                          </>
                        )}
                        {cat.description && (
                          <>
                            <span>•</span>
                            <span className="truncate max-w-xs text-slate-400 italic">
                              "{cat.description}"
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenCreate(cat.id)}
                      className="hidden sm:inline-flex items-center gap-1 text-xs rounded-xl border-slate-200 text-[#075E54] hover:bg-emerald-50"
                      title="Agregar subcategoría dentro de esta categoría"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Subcategoría
                    </Button>
                    <button
                      type="button"
                      onClick={() => handleOpenEdit(cat)}
                      className="p-2 text-slate-400 hover:text-[#075E54] hover:bg-emerald-50 rounded-xl transition-colors"
                      title="Editar categoría"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoryToDelete(cat)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                      title="Eliminar categoría"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Subcategories Container */}
                {hasChildren && isExpanded && (
                  <div className="bg-slate-50/70 border-t border-slate-100 pl-8 pr-4 sm:pl-14 sm:pr-6 py-2 divide-y divide-slate-100">
                    {cat.children!.map((sub) => (
                      <div key={sub.id} className="py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <ChevronRight className="w-4 h-4 text-emerald-600/40 shrink-0" />
                          <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-[#075E54] flex items-center justify-center shrink-0 shadow-2xs">
                            <Layers className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-slate-800 text-sm truncate">{sub.name}</h4>
                              <span
                                className={`px-1.5 py-0.2 text-[9px] font-bold uppercase rounded-full shrink-0 ${
                                  sub.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                                }`}
                              >
                                {sub.isActive ? 'Activa' : 'Inactiva'}
                              </span>
                              <code className="text-[10px] font-mono text-slate-400">
                                /{sub.slug || generateSlug(sub.name)}
                              </code>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Orden: {sub.sortOrder ?? 0} • {sub.productCount ?? 0} productos
                              {sub.description ? ` • ${sub.description}` : ''}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(sub)}
                            className="p-1.5 text-slate-400 hover:text-[#075E54] hover:bg-white rounded-lg transition-colors"
                            title="Editar subcategoría"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setCategoryToDelete(sub)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                            title="Eliminar subcategoría"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            )
          })}
        </section>
      )}

      {/* Modal de Creación / Edición */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !isSaving && setIsFormOpen(false)}
        title={editingCategory ? `Editar Categoría: ${editingCategory.name}` : 'Nueva Categoría'}
        size="lg"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {/* Nombre y Slug preview */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
              Nombre de la categoría *
            </label>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ej. Calzado Deportivo, Bebidas Calientes, Accesorios..."
              maxLength={80}
              autoFocus
              className="rounded-xl border-slate-200 text-sm py-2.5"
            />
            {formName.trim() && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <span>Ruta / Slug para catálogo:</span>
                <code className="bg-slate-100 text-[#075E54] font-semibold px-1.5 py-0.5 rounded font-mono text-[11px]">
                  /{generateSlug(formName)}
                </code>
              </p>
            )}
          </div>

          {/* Categoría Padre */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
              Categoría Padre (Jerarquía)
            </label>
            <select
              value={formParentId}
              onChange={(e) => setFormParentId(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
            >
              <option value="">Ninguna — Categoría Principal (Raíz)</option>
              {categoryOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {'— '.repeat(opt.depth)} {opt.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">
              Si seleccionas una categoría padre, esta categoría se convertirá en una subcategoría anidada.
            </p>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
              Descripción para la IA y Catálogo (Opcional)
            </label>
            <textarea
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Ej. Tenis para correr, zapatos de vestir y sandalias de temporada..."
              maxLength={300}
              rows={3}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20 resize-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              El asistente de WhatsApp consultará esta descripción para responder con precisión cuando los clientes pregunten por estos artículos.
            </p>
          </div>

          {/* Orden y Estado */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                Orden de aparición
              </label>
              <Input
                type="number"
                min={0}
                value={formSortOrder}
                onChange={(e) => setFormSortOrder(parseInt(e.target.value, 10) || 0)}
                className="rounded-xl border-slate-200 text-sm py-2"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Menor número aparece primero en el menú (0, 1, 2...).
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                Estado de publicación
              </label>
              <button
                type="button"
                onClick={() => setFormIsActive(!formIsActive)}
                className={`w-full flex items-center justify-between p-2.5 rounded-xl border transition-colors ${
                  formIsActive
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
              >
                <div className="text-left">
                  <p className="text-xs font-bold">{formIsActive ? 'Activa' : 'Inactiva'}</p>
                  <p className="text-[11px] text-slate-500">
                    {formIsActive ? 'Visible para clientes' : 'Oculta temporalmente'}
                  </p>
                </div>
                <div
                  className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-colors ${
                    formIsActive ? 'bg-[#075E54] justify-end' : 'bg-slate-300 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full shadow-xs" />
                </div>
              </button>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-2.5 pt-4 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsFormOpen(false)}
              disabled={isSaving}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSaving || !formName.trim()}
              className="bg-[#075E54] hover:bg-[#064E46] text-white font-semibold rounded-xl shadow-sm px-5"
            >
              {isSaving
                ? 'Guardando...'
                : editingCategory
                ? 'Actualizar categoría'
                : 'Crear categoría'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Confirmación de Eliminación */}
      <Modal
        isOpen={Boolean(categoryToDelete)}
        onClose={() => !deleteMutation.isPending && setCategoryToDelete(null)}
        title="Eliminar Categoría"
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-bold">¿Confirmas eliminar la categoría?</p>
              <p className="mt-1 text-xs text-red-700">
                Se eliminará <strong>"{categoryToDelete?.name}"</strong> de forma permanente.
              </p>
              {categoryToDelete?.children && categoryToDelete.children.length > 0 && (
                <p className="mt-2 text-xs font-semibold text-red-800 bg-red-100/80 p-2 rounded-xl">
                  ⚠️ Advertencia: Esta categoría contiene {categoryToDelete.children.length} subcategoría(s) asociadas.
                </p>
              )}
              {Boolean(categoryToDelete?.productCount) && (
                <p className="mt-1 text-xs text-red-700">
                  📦 Tiene {categoryToDelete?.productCount} producto(s) asignados.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCategoryToDelete(null)}
              disabled={deleteMutation.isPending}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => categoryToDelete && deleteMutation.mutate(categoryToDelete.id)}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-sm"
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar definitivamente'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function MetricCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm backdrop-blur">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 p-3 text-[#075E54]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}
